import SHA256 from 'crypto-js/sha256'
import { useAuthStore } from '@/stores/auth'
import type { PayWechatPrepayRequestBo } from '@/types/api'

export type PaymentTarget = Pick<PayWechatPrepayRequestBo, 'bizType' | 'bizNo'>
export type PaymentSurface =
  | { kind: 'packages'; groupCode?: string; offerCode?: string }
  | { kind: 'document'; documentId: string; conversationId: string | null }
  | { kind: 'order'; orderId: string }

const KEY_PREFIX = 'legal-web:payment-intent:v2:'
const WARMUP_PREFIX = 'legal-web:payment-warmup:'
const INTENT_PARAM = '_pay_intent'
const MAX_AGE = 5 * 60_000

export interface PaymentIntent {
  version: 2
  owner: string
  returnUrl: string
  createdAt: number
  surface: PaymentSurface
  /** 仅用户明确点击支付时保存业务单，提前准备身份不包含此字段。 */
  target?: PaymentTarget
}
export class PaymentIntentError extends Error {}
export const paymentOwner = (token: string) => SHA256(token).toString()

// 在路由渲染前捕获返回信息，避免清理地址栏后丢失弹窗恢复数据。
const arrivalUrl = new URL(window.location.href)
const ticketValue = new URLSearchParams(arrivalUrl.hash.slice(1)).get('wechat_pay_ticket')
export const paymentReturnTicket =
  ticketValue && /^[a-f0-9]{32}$/.test(ticketValue) ? ticketValue : null
let preview: PaymentIntent | null = null
let restorationDismissed = false
export function dismissPaymentRestoration() {
  restorationDismissed = true
}

export function surfaceKey(surface: PaymentSurface) {
  switch (surface.kind) {
    case 'packages':
      return `packages:${surface.groupCode || ''}:${surface.offerCode || ''}`
    case 'document':
      return `document:${surface.documentId}`
    case 'order':
      return `order:${surface.orderId}`
  }
}

export function getPaymentReturnSurface<K extends PaymentSurface['kind']>(
  kind: K,
): Extract<PaymentSurface, { kind: K }> | null {
  if (restorationDismissed) return null
  const token = useAuthStore.getState().accessToken
  const intent = token ? peekReturnIntent(token) : null
  return intent?.surface.kind === kind
    ? (intent.surface as Extract<PaymentSurface, { kind: K }>)
    : null
}

export function peekReturnIntent(token: string): PaymentIntent | null {
  if (!paymentReturnTicket) return null
  try {
    const id = arrivalUrl.searchParams.get(INTENT_PARAM)
    const value: unknown = preview ?? JSON.parse(sessionStorage.getItem(KEY_PREFIX + id) || 'null')
    if (!isIntent(value) || !isFresh(value) || value.owner !== paymentOwner(token)) return null
    const expected = new URL(value.returnUrl)
    const arrived = new URL(arrivalUrl)
    arrived.searchParams.delete(INTENT_PARAM)
    if (
      expected.origin !== window.location.origin ||
      expected.pathname !== arrived.pathname ||
      expected.search !== arrived.search
    )
      return null
    preview = value
    return value
  } catch {
    return null
  }
}

/** 清理片段凭据及内部流程参数，保留原业务页面、会话和锚点。 */
export function cleanReturnPath(token: string | null) {
  const intent = token ? peekReturnIntent(token) : null
  const url = new URL(intent?.returnUrl || arrivalUrl)
  url.searchParams.delete(INTENT_PARAM)
  if (!intent) url.hash = ''
  return url.pathname + url.search + url.hash
}

export function createPaymentIntent(
  target: PaymentTarget | undefined,
  token: string,
  surface: PaymentSurface,
) {
  const returnUrl = new URL(window.location.href)
  returnUrl.searchParams.delete(INTENT_PARAM)
  if (returnUrl.hash.startsWith('#wechat_pay_ticket=')) returnUrl.hash = ''
  if (surface.kind === 'document' && surface.conversationId) {
    returnUrl.searchParams.set('conversationId', surface.conversationId)
    returnUrl.searchParams.delete('sessionUuid')
    returnUrl.searchParams.delete('new')
  }
  const id = Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('')
  const intent: PaymentIntent = {
    target,
    surface,
    version: 2,
    owner: paymentOwner(token),
    returnUrl: returnUrl.href,
    createdAt: Date.now(),
  }
  const redirectUrl = new URL(returnUrl)
  redirectUrl.searchParams.set(INTENT_PARAM, id)
  return { id, intent, redirectUrl: redirectUrl.href }
}

export function savePaymentIntent(pending: ReturnType<typeof createPaymentIntent>) {
  try {
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index)
      if (!key?.startsWith(KEY_PREFIX)) continue
      try {
        const previous: unknown = JSON.parse(sessionStorage.getItem(key) || 'null')
        if (!isIntent(previous) || !isFresh(previous)) sessionStorage.removeItem(key)
      } catch {
        sessionStorage.removeItem(key)
      }
    }
    sessionStorage.setItem(KEY_PREFIX + pending.id, JSON.stringify(pending.intent))
  } catch {
    throw new PaymentIntentError('无法保存支付进度，请允许网页存储后重试')
  }
}

/** 先移除再校验；刷新或重复回调不会再次自动下单。 */
export function consumePaymentIntent(serverReturnUrl: string, token: string): PaymentIntent {
  const target = new URL(serverReturnUrl)
  if (target.origin !== window.location.origin) throw new PaymentIntentError('支付返回地址不正确')
  const id = target.searchParams.get(INTENT_PARAM)
  target.searchParams.delete(INTENT_PARAM)
  if (!id || !/^[a-f0-9]{32}$/.test(id)) throw new PaymentIntentError('支付进度已失效，请重新支付')
  let stored: unknown
  try {
    const raw = sessionStorage.getItem(KEY_PREFIX + id)
    sessionStorage.removeItem(KEY_PREFIX + id)
    stored = JSON.parse(raw || 'null')
  } catch {
    throw new PaymentIntentError('无法读取支付进度，请重新支付')
  }
  if (
    !isIntent(stored) ||
    !isFresh(stored) ||
    stored.owner !== paymentOwner(token) ||
    stored.returnUrl !== target.href
  ) {
    throw new PaymentIntentError('支付进度已失效或登录账号已变化，请重新支付')
  }
  return stored
}

/** 每个会话一分钟内只自动尝试一次，取消、网络失败或授权失败后不循环跳转。 */
export function claimWarmup(token: string) {
  try {
    const key = WARMUP_PREFIX + paymentOwner(token)
    const stored = sessionStorage.getItem(key)
    if (stored === 'blocked') return false
    const previous = Number(stored || 0)
    if (Date.now() - previous < 60_000) return false
    sessionStorage.setItem(key, String(Date.now()))
    return true
  } catch {
    return false
  }
}
export function markWarmupAttempt(token: string) {
  try {
    sessionStorage.setItem(WARMUP_PREFIX + paymentOwner(token), String(Date.now()))
  } catch {
    /* 存储受限时不自动重试。 */
  }
}
export function isFresh(value: PaymentIntent) {
  const age = Date.now() - value.createdAt
  return age >= 0 && age <= MAX_AGE
}
function isSurface(value: unknown): value is PaymentSurface {
  if (typeof value !== 'object' || value === null) return false
  const row = value as Record<string, unknown>
  return row.kind === 'packages'
    ? (row.offerCode === undefined || typeof row.offerCode === 'string') &&
        (row.groupCode === undefined || typeof row.groupCode === 'string')
    : row.kind === 'order'
      ? typeof row.orderId === 'string'
      : row.kind === 'document' &&
        typeof row.documentId === 'string' &&
        (row.conversationId === null || typeof row.conversationId === 'string')
}
function isIntent(value: unknown): value is PaymentIntent {
  if (typeof value !== 'object' || value === null) return false
  const row = value as Record<string, unknown>
  const target = row.target as Record<string, unknown> | undefined
  return (
    row.version === 2 &&
    typeof row.owner === 'string' &&
    typeof row.returnUrl === 'string' &&
    typeof row.createdAt === 'number' &&
    Number.isFinite(row.createdAt) &&
    isSurface(row.surface) &&
    (target === undefined ||
      (target !== null &&
        typeof target === 'object' &&
        typeof target.bizType === 'string' &&
        target.bizType.length > 0 &&
        target.bizType.length <= 64 &&
        typeof target.bizNo === 'string' &&
        target.bizNo.length > 0 &&
        target.bizNo.length <= 128))
  )
}

/** 离开去授权后先禁止自动重试；只有成功返回或用户再次主动支付才解除。 */
export function blockWarmup(token: string) {
  try {
    sessionStorage.setItem(WARMUP_PREFIX + paymentOwner(token), 'blocked')
  } catch {
    /* 存储受限时保留手动支付。 */
  }
}
